import { useCallback, useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Settings2, RefreshCw, Sparkles, RotateCcw } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../utils/api';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useRoleDashboard } from '../../hooks/useRoleDashboard';
import { WidgetGrid } from './widgets/WidgetGrid';
import { WidgetSelector } from './widgets/WidgetSelector';
import { WIDGET_REGISTRY } from './widgets/widgetRegistry';
import { resolveRoleKey, type DashboardRoleKey } from './roleLayouts';
export function LegacyWidgetDashboard() {
  const { user } = useAuthStore();

  const {
    roleLayout,
    enabledWidgetIds,
    layouts,
    loading: layoutLoading,
    isCustom,
    handleLayoutChange,
    toggleWidget,
    resetToDefault,
  } = useRoleDashboard();

  const { data, loading: widgetDataLoading, error, refetch } = useWidgetData();
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [isCustomizing, setIsCustomizing] = useState(false);

  useEffect(() => {
    api
      .get('/credits/balance')
      .then((res) => {
        if (res.data?.data?.balance != null) setCreditBalance(res.data.data.balance);
      })
      .catch(() => {});
  }, []);

  const openCustomizer = useCallback(() => setIsCustomizing(true), []);
  const closeCustomizer = useCallback(() => setIsCustomizing(false), []);

  // Widgets the registry actually knows how to render — silently drop
  // unknowns so the grid doesn't render blank tiles.
  const knownWidgetIds = new Set(WIDGET_REGISTRY.map((w) => w.id));
  const renderableIds = enabledWidgetIds.filter((id) => knownWidgetIds.has(id));
  const allowedIds = new Set(knownWidgetIds);

  const firstName = (user as any)?.firstName ?? '';
  const subtitleRendered =
    firstName && roleLayout.subtitle.length > 0
      ? `${firstName} — ${roleLayout.subtitle}`
      : roleLayout.subtitle;

  return (
    <div className="min-h-full">
      {/* Header — reflects the role layout's title + subtitle */}
      <div className="mb-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{roleLayout.title}</h1>
          <p className="mt-1 text-sm text-gray-500">{subtitleRendered}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refetch}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
            title="Refresh data"
          >
            <RefreshCw size={14} className={widgetDataLoading ? 'animate-spin' : ''} />
          </button>
          {isCustom && (
            <button
              onClick={() => void resetToDefault()}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
              title="Reset to default role layout"
            >
              <RotateCcw size={14} />
              Reset
            </button>
          )}
          <button
            onClick={openCustomizer}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            style={{ background: 'var(--ink-0)', color: 'var(--ivory-0)', border: '1px solid var(--ink-0)' }}
          >
            <Settings2 size={16} />
            Customize
          </button>
        </div>
      </div>

      {/* AI Credit Balance Banner */}
      {creditBalance != null && (
        <Link
          to="/credits"
          className="mb-4 flex flex-col items-start gap-2 rounded-lg border border-blue-500/20 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 px-4 py-2.5 transition-colors hover:from-blue-500/10 hover:to-indigo-500/10 sm:flex-row sm:items-center"
        >
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-blue-400" />
            <span className="text-sm font-medium text-[var(--text-secondary)]">
              AI Credits:{' '}
              <span className="font-semibold text-blue-400">
                {creditBalance.toLocaleString('en-IN')}
              </span>{' '}
              available
            </span>
          </div>
          <span className="mt-1 flex sm:mt-0 sm:ml-auto text-xs text-[var(--text-muted)] hover:underline">
            View details &rarr;
          </span>
        </Link>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
          <button onClick={refetch} className="ml-2 font-medium text-red-800 underline">
            Retry
          </button>
        </div>
      )}

      {/* Loading spinner on first paint while we resolve persisted layout */}
      {layoutLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
        </div>
      ) : (
        <WidgetGrid
          enabledWidgetIds={renderableIds}
          layouts={layouts}
          data={data}
          loading={widgetDataLoading}
          onLayoutChange={handleLayoutChange}
        />
      )}

      {/* Empty state */}
      {!layoutLoading && renderableIds.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-16">
          <Settings2 size={40} className="mb-3 text-gray-300" />
          <p className="text-sm text-gray-500">No widgets enabled for {roleLayout.title}</p>
          <button
            onClick={openCustomizer}
            className="mt-2 text-sm font-medium text-[var(--ink-0)] hover:underline"
          >
            Customize your dashboard
          </button>
        </div>
      )}

      {/* Widget Selector slide-over */}
      {isCustomizing && (
        <WidgetSelector
          enabledIds={enabledWidgetIds}
          allowedIds={allowedIds}
          onToggle={(id) => void toggleWidget(id)}
          onReset={() => void resetToDefault()}
          onClose={closeCustomizer}
        />
      )}
    </div>
  );
}
